import React, { useMemo, useCallback } from "react";
import { Pagination, Select, Loading, Button, Input, RadioButton, SearchInput, SimpleTooltip, Badge } from "../../common";
import { TableColumn } from "../../common/Table";
import type { IPermission } from "../../../types/rbac";
import { PermissionNodeType } from "../../../types/rbac";
import PermissionTree from "./PermissionTree";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import PermissionGate from "../../common/PermissionGate";
import { Edit, FileText, RotateCcw, UserPlus, Eye, Trash2 } from "lucide-react";
import { TextArea } from "../../common/TextArea";
import { SelectOption } from "../../common/Select";

export type RoleType = "SuperAdmin" | "Admin" | "HR" | "Employee" | "Manager";

/** Aligned with v2 IRoleResponse */
export type RoleRecord = {
  id: string;
  name: string;
  type?: RoleType;
  description?: string;
  permissions: string[];
  isActive: boolean;
  createdBy?: string;
};

type RoleFormCardProps = {
  roleName: string;
  onRoleNameChange: (value: string) => void;
  roleType: RoleType | "";
  roleTypeOptions: SelectOption[];
  onRoleTypeChange: (value: RoleType | "") => void;
  isActive: boolean;
  onStatusChange: (value: boolean) => void;
  roleDesc: string;
  onRoleDescChange: (value: string) => void;
  onSubmit: () => void;
  saving: boolean;
  isEditing?: boolean;
  onCancel?: () => void;
};

export const RoleFormCard: React.FC<RoleFormCardProps> = ({
  roleName,
  onRoleNameChange,
  roleType,
  roleTypeOptions,
  onRoleTypeChange,
  isActive,
  onStatusChange,
  roleDesc,
  onRoleDescChange,
  onSubmit,
  saving,
  isEditing = false,
  onCancel,
}) => (
  <>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-1">
        <h2 className="text-lg font-bold text-slate-900">
          {isEditing ? "Update Role" : "Create Role"}
        </h2>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Input
          label="Role Name"
          required
          placeholder="Enter role name"
          value={roleName}
          onChange={(value) => onRoleNameChange(value as string)}
        />
      </div>
      <div className="space-y-2">
        <Select
          label="Role Type"
          required
          options={roleTypeOptions}
          value={roleType}
          onChange={(value) => onRoleTypeChange((value as RoleType) || "")}
          placeholder="Select type"
          clearable
        />
      </div>
    </div>
    <div>
      <RadioButton
        name="roleActive"
        label="Status"
        required
        value={isActive ? "active" : "inactive"}
        onChange={(val) => onStatusChange(val === "active")}
        options={[
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ]}
      />
    </div>
    <div className="space-y-2">
      <TextArea
        label="Description"
        placeholder="Optional notes about this role"
        value={roleDesc}
        onChange={(value) => onRoleDescChange(value)}
        minRows={4}
      />
    </div>
    <div className="flex items-center justify-end gap-2">
      {isEditing && onCancel && (
        <Button appearance="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      )}
      <Button
        appearance="primary"
        onClick={onSubmit}
        disabled={saving}
        icon={
          isEditing ? (
            <Edit className="w-5 h-5 " />
          ) : (
            <FileText className="w-5 h-5" />
          )
        }
      >
        {saving ? (isEditing ? "Updating..." : "Saving...") : "Submit"}
      </Button>
    </div>
  </>
);

type PermissionCardProps = {
  title?: string;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  selectedCount: number;
  tree: IPermission[];
  value: string[];
  onChange: (ids: string[]) => void;
  lockedIds?: Set<string>;
  disabled?: boolean;
  isBusy?: boolean;
  rounded?: string;
  busyMessage?: string;
};

export const PermissionCard: React.FC<PermissionCardProps> = ({
  title,
  icon,
  description,
  selectedCount,
  tree,
  value,
  onChange,
  lockedIds,
  disabled,
  isBusy,
  rounded = "rounded-2xl",
  busyMessage,
}) => (
  <div className={`${rounded} bg-white border border-slate-200 shadow-soft p-6 flex flex-col h-full gap-5`}>
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {icon && (
            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-500 flex items-center justify-center">
              {icon}
            </div>
          )}
          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            {description && (
              <p className="text-sm text-slate-500 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 border border-indigo-100 px-3 py-1 rounded-full ">
          {selectedCount} node{selectedCount === 1 ? "" : "s"} selected
        </span>
      </div>
    </div>
    <div className="relative border border-slate-100 rounded-xl bg-slate-50 p-3 flex-1 overflow-auto shadow-inner max-h-[600px] min-h-[400px]">
      {isBusy && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <Loading size="md" message={busyMessage} centered />
        </div>
      )}
      <PermissionTree
        tree={tree}
        value={value}
        onChange={onChange}
        lockedIds={lockedIds}
        disabled={disabled}
      />
    </div>
  </div>
);

type RoleTableCardProps = {
  roles: RoleRecord[];
  totalRoles: number;
  roleColumns: TableColumn<RoleRecord>[];
  rolesLoading: boolean;
  rolePage: number;
  rolePageSize: number;
  onRolePageChange: (page: number) => void;
  onRolePageSizeChange: (size: number) => void;
};

export const RoleTableCard: React.FC<RoleTableCardProps> = ({
  roles,
  totalRoles,
  roleColumns,
  rolesLoading,
  rolePage,
  rolePageSize,
  onRolePageChange,
  onRolePageSizeChange,
}) => (
  <div className="bg-white rounded-xl shadow-soft border border-slate-200 flex flex-col">
    <ConfigurableTable<RoleRecord>
      columns={roleColumns}
      data={roles}
      loading={rolesLoading}
      emptyMessage="No roles found. Create a role to get started."
      rowKey="id"
      size="sm"
      configOptions={{ persistenceKey: "access-rights-role-table" }}
      renderColumnSelector={(selector) => (
        <div className="px-6 py-4 border-b border-slate-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-slate-900">
              Roles ({totalRoles})
            </h3>
          </div>
          {selector}
        </div>
      )}
    />
    {totalRoles > 0 && (
      <Pagination
        currentPage={rolePage}
        totalItems={totalRoles}
        itemsPerPage={rolePageSize}
        onPageChange={onRolePageChange}
        onItemsPerPageChange={onRolePageSizeChange}
        itemsPerPageOptions={[5, 10, 20, 50, 100]}
        className="rounded-none border-x-0 border-b-0 border-t"
      />
    )}
  </div>
);

type UserAssignmentCardProps = {
  userOptions: SelectOption[];
  roleOptions: SelectOption[];
  selectedUserIds: string[];
  selectedUserId: string | undefined;
  setSelectedUserid: (value: string | undefined) => void;
  selectedRoleId: string | undefined;
  onUserChange: (value: string[]) => void;
  onRoleChange: (value: string) => void;
  onReset: () => void;
  onAssign: () => void;
  isBulkAssignLoading: boolean;
  canAssign: boolean;
  rolePrefillLoading: boolean;
};

export const UserAssignmentCard: React.FC<UserAssignmentCardProps> = ({
  userOptions,
  roleOptions,
  selectedUserIds,
  selectedUserId,
  setSelectedUserid,
  selectedRoleId,
  onUserChange,
  onRoleChange,
  onReset,
  onAssign,
  isBulkAssignLoading,
  canAssign,
  rolePrefillLoading,
}) => (
  <div className="bg-[var(--color-bg-base)] rounded-[16px] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-6 space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Role Assignment</h2>
        <p className="text-sm text-[var(--color-text-secondary)]">Assign active roles to users.</p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 ">
      <div className="space-y-2">
        <Select
          value={selectedUserIds.length ? selectedUserIds : (selectedUserId ? [selectedUserId] : [])}
          label="Select Employee"
          options={userOptions}
          onChange={(value) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              onUserChange([]);
              setSelectedUserid("");
              return;
            }
            const ids = Array.isArray(value) ? value.map(String) : [];
            onUserChange(ids);
          }}
          required
          placeholder="Select Employees..."
          searchable
          multiple
          clearable
        />
      </div>
      <div className="space-y-2">
        <Select
          label="Role"
          options={roleOptions}
          value={selectedRoleId}
          onChange={(value) => onRoleChange(value ? String(value) : "")}
          required
          placeholder="Search roles..."
          searchable
          clearable
          disabled={!selectedUserIds.length && !selectedUserId}
          loading={rolePrefillLoading}
        />
      </div>
    </div>
    <div className="flex items-center justify-end gap-2">
      <Button
        appearance="secondary"
        onClick={onReset}
        size="middle"
        icon={<RotateCcw className="w-5 h-5" />}
      >
        Reset
      </Button>
      <Button
        appearance="primary"
        onClick={onAssign}
        disabled={!canAssign && !isBulkAssignLoading}
        loading={isBulkAssignLoading}
        size="middle"
        icon={!isBulkAssignLoading ? <UserPlus className="w-5 h-5" /> : undefined}
      >
        {isBulkAssignLoading ? "Assigning..." : "Assign Role"}
      </Button>
    </div>
  </div>
);

type OverridesSidebarProps = {
  userOptions: SelectOption[];
  selectedUserId: string | undefined;
  onUserChange: (value: string) => void;
  onSubmit: () => void;
  disableSubmit: boolean;
  overridesSaving: boolean;
};

export const OverridesSidebar: React.FC<OverridesSidebarProps> = ({
  userOptions,
  selectedUserId,
  onUserChange,
  onSubmit,
  disableSubmit,
  overridesSaving,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-6 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900">
          Additional Permissions
        </h2>
        <p className="text-sm text-slate-500">
          Layer extra permissions on top of a user&rsquo;s base role. Locked
          permissons indicate inherited privileges.
        </p>
      </div>
      <div className="space-y-2">
        <Select
          label="Select Employee"
          required
          options={userOptions}
          value={selectedUserId}
          onChange={(value) => onUserChange(value ? String(value) : "")}
          placeholder="Search Employee..."
          searchable
          clearable
        />
      </div>
      <PermissionGate code={PERMISSIONS.ACCESS_RIGHT_MANAGE}>
        <div className="flex justify-end">
          <Button
            appearance="primary"
            onClick={onSubmit}
            disabled={disableSubmit}
            icon={<FileText className="w-5 h-5" />}
          >
            {overridesSaving ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </PermissionGate>
    </div>
  );
};

/* ─── Helpers ─────────────────────────────────────────────── */

type ModulePermInfo = {
  moduleId: string;
  moduleName: string;
  viewActionId: string | null;
  editActionIds: string[];
};

const getModulePermissions = (
  tree: IPermission[],
  nodeId: string,
  collected: ModulePermInfo[],
): void => {
  const node = tree.find((n) => n.id === nodeId);
  if (!node || !node.children) return;

  const actions = node.children.filter(
    (ch) => ch.type === PermissionNodeType.ACTION,
  );
  if (actions.length > 0) {
    const view = actions.find(
      (a) => a.name.toLowerCase() === "view",
    );
    const edits = actions.filter(
      (a) => a.name.toLowerCase() !== "view",
    );
    collected.push({
      moduleId: node.id,
      moduleName: node.name,
      viewActionId: view?.id ?? null,
      editActionIds: edits.map((e) => e.id),
    });
  }

  node.children
    .filter((ch) => ch.type !== PermissionNodeType.ACTION)
    .forEach((ch) => getModulePermissions(ch.children || [], ch.id, collected));
};

const collectAllActions = (node: IPermission): IPermission[] => {
  const actions: IPermission[] = [];
  const walk = (n: IPermission) => {
    if (n.type === PermissionNodeType.ACTION) {
      actions.push(n);
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(node);
  return actions;
};

const flattenModules = (tree: IPermission[]): ModulePermInfo[] => {
  const result: ModulePermInfo[] = [];

  const walk = (nodes: IPermission[]) => {
    for (const node of nodes) {
      if (
        node.type === PermissionNodeType.MODULE ||
        node.type === PermissionNodeType.ROOT
      ) {
        const allActions = collectAllActions(node);
        if (allActions.length > 0) {
          const view = allActions.find(
            (a) => a.name.toLowerCase() === "view",
          );
          const edits = allActions.filter(
            (a) => a.name.toLowerCase() !== "view",
          );
          result.push({
            moduleId: node.id,
            moduleName: node.name,
            viewActionId: view?.id ?? null,
            editActionIds: edits.map((e) => e.id),
          });
        }
      }
      if (node.children) walk(node.children);
    }
  };

  walk(tree);
  return result;
};

/* ─── RoleListCard ────────────────────────────────────────── */

export type RoleListCardProps = {
  roles: RoleRecord[];
  rolesLoading: boolean;
  selectedRoleId: string | null;
  onSelectRole: (role: RoleRecord) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onEditRole: (roleId: string) => void;
  onViewRole: (roleId: string) => void;
  onDeleteRole: (role: RoleRecord) => void;
  canManage: boolean;
};

export const RoleListCard: React.FC<RoleListCardProps> = ({
  roles,
  rolesLoading,
  selectedRoleId,
  onSelectRole,
  searchQuery,
  onSearchChange,
  onEditRole,
  onViewRole,
  onDeleteRole,
  canManage,
}) => (
  <div className="bg-white rounded-[16px] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] flex flex-col h-full">
    <div className="px-5 py-4 border-b border-[var(--color-border-primary)]">
      <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">Roles</h3>
      <SearchInput
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Search roles..."
      />
    </div>

    <div className="flex-1 overflow-y-auto">
      {rolesLoading ? (
        <div className="p-8 flex justify-center">
          <Loading size="md" centered />
        </div>
      ) : roles.length === 0 ? (
        <div className="p-8 text-center text-sm text-[var(--color-text-tertiary)]">
          No roles found.
        </div>
      ) : (
        <div className="divide-y divide-[var(--color-border-primary)]">
          {roles.map((role) => {
            const isSelected = selectedRoleId === role.id;
            return (
              <div
                key={role.id}
                className={`flex items-center gap-2 px-5 py-3 transition-colors ${
                  isSelected
                    ? "bg-[var(--color-bg-muted)] border-l-2 border-[var(--color-primary-600)]"
                    : "border-l-2 border-transparent hover:bg-[var(--color-bg-muted)]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelectRole(role)}
                  className="flex-1 min-w-0 text-left cursor-pointer"
                >
                  <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                    {role.name}
                  </p>
                  {role.description && (
                    <p className="text-xs text-[var(--color-text-tertiary)] truncate mt-0.5">
                      {role.description}
                    </p>
                  )}
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {canManage && (
                    <>
                      <SimpleTooltip label="Edit" side="top">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEditRole(role.id); }}
                          className="p-1.5 rounded-md text-[var(--color-text-tertiary)] hover:text-green-600 hover:bg-[var(--color-bg-muted)] transition-colors"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </SimpleTooltip>
                      <SimpleTooltip label="Delete" side="top">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDeleteRole(role); }}
                          className="p-1.5 rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg-muted)] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </SimpleTooltip>
                    </>
                  )}
                  <SimpleTooltip label="View" side="top">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onViewRole(role.id); }}
                      className="p-1.5 rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-primary-600)] hover:bg-[var(--color-bg-muted)] transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </SimpleTooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

/* ─── PermissionsMatrixCard ───────────────────────────────── */

type PermissionsMatrixCardProps = {
  selectedRole: RoleRecord | null;
  tree: IPermission[];
  onViewRole: (roleId: string) => void;
  onEditRole: (roleId: string) => void;
  canManage: boolean;
};

export const PermissionsMatrixCard: React.FC<PermissionsMatrixCardProps> = ({
  selectedRole,
  tree,
  onViewRole,
  onEditRole,
  canManage,
}) => {
  const modules = useMemo(() => flattenModules(tree), [tree]);

  const selectedIdsSet = useMemo(
    () => new Set(selectedRole?.permissions?.map(String) ?? []),
    [selectedRole],
  );

  const getModuleState = useCallback(
    (mod: ModulePermInfo) => {
      const viewOn = mod.viewActionId
        ? selectedIdsSet.has(mod.viewActionId)
        : false;
      const editOn = mod.editActionIds.every((id) => selectedIdsSet.has(id));
      const editPartial = mod.editActionIds.some((id) =>
        selectedIdsSet.has(id),
      );
      return { viewOn, editOn, editPartial };
    },
    [selectedIdsSet],
  );

  if (!selectedRole) {
    return (
      <div className="bg-white rounded-[16px] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] flex flex-col items-center justify-center h-full p-6">
        <div className="w-14 h-14 rounded-full bg-[var(--color-bg-muted)] flex items-center justify-center mb-4">
          <FileText className="w-6 h-6 text-[var(--color-text-tertiary)]" />
        </div>
        <p className="text-base font-semibold text-[var(--color-text-primary)] mb-1">
          No Role Selected
        </p>
        <p className="text-sm text-[var(--color-text-tertiary)] text-center max-w-[280px]">
          Select a role from the list to view its permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[16px] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] flex flex-col h-full">
      <div className="px-5 py-4 border-b border-[var(--color-border-primary)]">
        <h3 className="text-base font-bold text-[var(--color-text-primary)]">
          {selectedRole.name}
        </h3>
        <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
          {selectedRole.type || "Role"} &middot;{" "}
          {selectedRole.isActive !== false ? "Active" : "Inactive"}
          {selectedRole.description && (
            <>&nbsp;&middot; <span className="truncate max-w-[200px] inline-block align-bottom">{selectedRole.description}</span></>
          )}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {modules.length === 0 ? (
          <div className="text-center text-sm text-[var(--color-text-tertiary)] py-8">
            No modules available.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[1fr,100px,120px] bg-[var(--color-bg-muted)] border-b border-[var(--color-border-primary)]">
              <div className="px-5 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                Module
              </div>
              <div className="py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">
                View
              </div>
              <div className="py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">
                View &amp; Edit
              </div>
            </div>
            <div className="divide-y divide-[var(--color-border-primary)]">
              {modules.map((mod) => {
                const { viewOn, editOn, editPartial } = getModuleState(mod);
                return (
                  <div
                    key={mod.moduleId}
                    className="grid grid-cols-[1fr,100px,120px] hover:bg-[var(--color-bg-muted)] transition-colors"
                  >
                    <div className="px-5 py-3 flex items-center">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">
                        {mod.moduleName}
                      </span>
                    </div>
                    <div className="py-3 flex items-center justify-center">
                      {viewOn ? (
                        <span className="w-5 h-5 rounded-full bg-[var(--color-primary-600)] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      ) : (
                        <span className="w-5 h-5 rounded-full border-2 border-[var(--color-border-primary)]" />
                      )}
                    </div>
                    <div className="py-3 flex items-center justify-center">
                      {editOn ? (
                        <span className="w-5 h-5 rounded-full bg-[var(--color-primary-600)] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      ) : editPartial ? (
                        <span className="w-5 h-5 rounded-full bg-[var(--color-primary-300)] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                          </svg>
                        </span>
                      ) : (
                        <span className="w-5 h-5 rounded-full border-2 border-[var(--color-border-primary)]" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
